/* Storefront diagnostics only. Native Shopify integrations own checkout/purchase events. */
(() => {
  const script = document.currentScript;
  const home = script?.dataset.homePath || '/';
  const onHome = script?.dataset.pageType === 'index';
  const now = Date.now();
  let internal = false;
  try { internal = new URL(document.referrer).origin === location.origin; } catch (_) {}
  // Functional visit state only: no identifiers or URLs stored or sent.
  let homepageEntry = onHome && !internal;
  try {
    const saved = JSON.parse(sessionStorage.getItem('cp-visit-entry-v1') || 'null');
    homepageEntry = saved && now - saved.at < 30 * 60 * 1000 ? saved.homepageEntry === true : homepageEntry;
    sessionStorage.setItem('cp-visit-entry-v1', JSON.stringify({homepageEntry, at: now}));
  } catch (_) { /* Storage unavailable: internal navigation cannot trigger a popup. */ }
  window.CatterpodVisit = {homepageEntry: homepageEntry && onHome && location.pathname === home};
  if (window.CatterpodFunnel) return;
  const allowedEvents = new Set(['recommended_visible','recommended_click','color_selected','cart_add_success','cart_add_error','checkout_click','checkout_blocked','checkout_handoff']);
  const queue = [];
  const qa = new URLSearchParams(location.search).get('utm_source') === 'qa';
  const cards = document.querySelectorAll('[data-cp-recommended-set]');
  const reportedCards = new WeakSet();
  const pendingCards = new Set();
  let visible = null;
  let retryTimer = null;
  let loadingPrivacy = false;
  let privacyLoadAttempts = 0;
  const privacyReady = () => typeof window.Shopify?.customerPrivacy?.analyticsProcessingAllowed === 'function';
  // Shopify's Allowed method includes region and merchant settings. Missing/failed
  // initialization is unknown, not consent. Never set consent on the visitor's behalf.
  const permitted = () => {
    try {
      return !window.Shopify?.designMode && privacyReady() &&
        window.Shopify.customerPrivacy.analyticsProcessingAllowed() === true;
    } catch (_) { return false; }
  };
  const clearQueue = () => { queue.length = 0; pendingCards.clear(); };
  const markReported = card => {
    if (!card) return;
    pendingCards.delete(card);
    reportedCards.add(card);
    visible?.unobserve(card);
  };
  const send = (name, fields) => {
    // Use the installed Clarity instance; do not create another tracker or override consent.
    if (!permitted() || typeof window.clarity !== 'function') return false;
    for (const [key, value] of Object.entries(fields)) window.clarity('set', 'cp_' + key, value);
    window.clarity('event', 'cp_' + name);
    if (qa) {
      window.clarity('set', 'cp_traffic', 'qa');
      console.info('Catterpod funnel sent: cp_' + name);
    }
    // Event-specific names preserve card attribution; Clarity tags are session filters, not event properties.
    if (fields.set && ['recommended_visible','recommended_click'].includes(name)) {
      window.clarity('event', 'cp_' + name + '_' + fields.set);
    }
    return true;
  };
  const track = (name, context = {}, card = null) => {
    try {
      if (!allowedEvents.has(name)) return;
      if (!permitted()) {
        clearQueue();
        if (!privacyReady()) { loadPrivacy(); startRetry(); }
        return;
      }
      const fields = {};
      for (const key of ['set','position','color','variant','source','reason']) {
        const value = String(context[key] || '');
        if (/^[a-zA-Z0-9_-]{1,64}$/.test(value)) fields[key] = value;
      }
      if (send(name, fields)) markReported(card);
      else if (queue.length < 30) {
        queue.push([name, fields, card]);
        if (card) { pendingCards.add(card); visible?.unobserve(card); }
        startRetry();
      }
    } catch (_) { /* Analytics must never interrupt shopping. */ }
  };
  window.CatterpodFunnel = {track};

  function refreshPrivacy() {
    try {
      if (!permitted()) {
        clearQueue();
        visible?.disconnect();
        visible = null;
        return;
      }
      // A fresh observer evaluates the current viewport after API readiness or
      // consent acceptance. No pre-consent visibility/click history is replayed.
      if (!visible && 'IntersectionObserver' in window) {
        const observer = new IntersectionObserver(entries => {
          try {
            if (visible !== observer || !permitted()) return;
            entries.forEach(entry => {
              const card = entry.target;
              if (entry.isIntersecting && entry.intersectionRatio >= 0.5 &&
                  !reportedCards.has(card) && !pendingCards.has(card)) {
                track('recommended_visible', {set:card.dataset.cpRecommendedSet,position:card.dataset.cpCardPosition}, card);
              }
            });
          } catch (_) { /* Observation must never interrupt shopping. */ }
        }, {threshold:0.5});
        visible = observer;
        cards.forEach(card => { if (!reportedCards.has(card)) observer.observe(card); });
      }
      while (queue.length && permitted() && typeof window.clarity === 'function') {
        const [name, fields, card] = queue.shift();
        if (send(name, fields)) markReported(card);
      }
    } catch (_) { clearQueue(); }
  }
  function loadPrivacy() {
    try {
      if (window.Shopify?.designMode || privacyReady() || loadingPrivacy || privacyLoadAttempts >= 3 ||
          typeof window.Shopify?.loadFeatures !== 'function') return;
      loadingPrivacy = true;
      privacyLoadAttempts++;
      // Supported storefront API initialization; this does not collect/set consent.
      window.Shopify.loadFeatures([{name:'consent-tracking-api',version:'0.2'}], error => {
        loadingPrivacy = false;
        if (!error) refreshPrivacy();
        if (!privacyReady() || queue.length) startRetry();
      });
    } catch (_) { loadingPrivacy = false; }
  }
  function startRetry() {
    if (retryTimer !== null || window.Shopify?.designMode) return;
    let attempts = 0;
    retryTimer = setInterval(() => {
      loadPrivacy();
      refreshPrivacy();
      if (++attempts >= 30 || (privacyReady() && !queue.length)) {
        clearInterval(retryTimer);
        retryTimer = null;
      }
    }, 1000);
  }
  document.addEventListener('visitorConsentCollected', () => {
    loadPrivacy();
    refreshPrivacy();
    if (!privacyReady() || queue.length) startRetry();
  });
  loadPrivacy();
  refreshPrivacy();
  if (!privacyReady()) startRetry();
  document.addEventListener('click', event => {
    try {
      const link = event.target?.closest?.('a');
      const card = link?.closest('[data-cp-recommended-set]');
      if (card && link.getAttribute('href')?.includes('/products/')) {
        track('recommended_click', {set:card.dataset.cpRecommendedSet,position:card.dataset.cpCardPosition});
      }
    } catch (_) { /* Diagnostics do not own the navigation. */ }
  });
  document.addEventListener('change', event => {
    try {
      const field = event.target;
      if (!field?.closest?.('variant-selects [data-cp-color-option]')) return;
      const color = String(field.value).toLowerCase();
      if (['storm','dune','cloud'].includes(color)) track('color_selected', {color});
    } catch (_) { /* Diagnostics do not own the variant selection. */ }
  });
})();
